import React from "react";
import { LogOut } from "lucide-react";
import { performLogout } from "../lib/logout.js";

type Props = {
  /**
   * Visual style:
   *   - "menuitem" : full-width row inside the user dropdown (text + leading icon)
   *   - "icon"     : square icon-only button (e.g. for collapsed sidebars)
   */
  variant?: "menuitem" | "icon";
  /**
   * Where to send the user after logout. Defaults to "/login" — callers
   * generally don't override, but a few flows (admin tools) want
   * "/login?return_to=/dashboard/" so the next sign-in lands back on
   * the dashboard.
   */
  redirectTo?: string;
  /** Optional callback fired before redirect (e.g. close a dropdown). */
  onBeforeLogout?: () => void;
  className?: string;
};

export default function LogoutButton({
  variant = "menuitem",
  redirectTo = "/login",
  onBeforeLogout,
  className,
}: Props): React.ReactElement {
  const [pending, setPending] = React.useState(false);

  const handle = async () => {
    if (pending) return;
    setPending(true);
    onBeforeLogout?.();
    // performLogout never resolves before the page navigates; even so we
    // await it so a slow API call doesn't leave the button enabled and
    // re-clickable.
    await performLogout(redirectTo);
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={() => void handle()}
        disabled={pending}
        className={`btn-ghost h-9 w-9 p-0 text-danger ${className ?? ""}`.trim()}
        aria-label="登出"
        title="登出"
      >
        <LogOut className="h-5 w-5" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void handle()}
      disabled={pending}
      className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-danger hover:bg-bg-hover disabled:opacity-50 ${className ?? ""}`.trim()}
      role="menuitem"
    >
      <LogOut className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
      <span>{pending ? "登出中…" : "登出"}</span>
    </button>
  );
}
